import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_VOICE_SECONDS = 180;

const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  const options = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return options.find(m => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) || '';
};

/**
 * Records a voice message in the browser.
 * state: idle | recording ; levels: last ~28 live levels for the on-screen meter.
 * stop() resolves to { blob, mime, duration, waveform } (waveform = 48 bars 0..1), cancel() discards.
 * Errors: 'insecure' (page not on HTTPS), 'unsupported', 'denied', 'nodevice'.
 */
export default function useVoiceRecorder() {
  const [state, setState] = useState('idle');
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState([]);
  const r = useRef({});

  const cleanup = useCallback(() => {
    const c = r.current;
    if (c.raf) cancelAnimationFrame(c.raf);
    if (c.timer) clearInterval(c.timer);
    if (c.stream) c.stream.getTracks().forEach(t => t.stop());
    if (c.ctx && c.ctx.state !== 'closed') c.ctx.close().catch(() => {});
    r.current = {};
    setState('idle'); setElapsed(0); setLevels([]);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const finish = useCallback((keep) => new Promise((resolve) => {
    const c = r.current;
    if (!c.rec) { cleanup(); resolve(null); return; }
    c.rec.onstop = () => {
      const duration = (performance.now() - c.started) / 1000;
      const samples = c.samples || [];
      const N = 48;
      const peak = Math.max(0.0001, ...samples);
      const waveform = Array.from({ length: N }, (_, i) => {
        const a = Math.floor((i * samples.length) / N), b = Math.max(a + 1, Math.floor(((i + 1) * samples.length) / N));
        const seg = samples.slice(a, b);
        const v = seg.length ? Math.max(...seg) / peak : 0;
        return Math.round(Math.min(1, Math.max(0.06, v)) * 1000) / 1000;
      });
      const mime = c.rec.mimeType || c.mime || 'audio/webm';
      const blob = new Blob(c.chunks, { type: mime });
      cleanup();
      resolve(keep && blob.size > 0 && duration >= 0.6 ? { blob, mime, duration, waveform } : null);
    };
    try { c.rec.stop(); } catch (e) { cleanup(); resolve(null); }
  }), [cleanup]);

  const start = useCallback(async (onAutoStop) => {
    if (!window.isSecureContext) throw new Error('insecure');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') throw new Error('unsupported');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (e) {
      throw new Error(e && (e.name === 'NotFoundError' || e.name === 'OverconstrainedError') ? 'nodevice' : 'denied');
    }
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 48000 } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 1024;
    src.connect(an);
    const buf = new Float32Array(an.fftSize);
    const samples = [];
    let lastPush = 0;
    const loop = (t) => {
      an.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      if (t - lastPush > 60) {
        lastPush = t;
        samples.push(rms);
        setLevels(prev => [...prev.slice(-27), Math.min(1, rms * 5)]);
      }
      r.current.raf = requestAnimationFrame(loop);
    };

    r.current = { stream, rec, chunks, ctx, samples, mime, started: performance.now() };
    rec.start(250);
    r.current.raf = requestAnimationFrame(loop);
    r.current.timer = setInterval(() => {
      const s = (performance.now() - r.current.started) / 1000;
      setElapsed(s);
      if (s >= MAX_VOICE_SECONDS && onAutoStop) onAutoStop();
    }, 200);
    setState('recording');
  }, []);

  return { state, elapsed, levels, start, stop: () => finish(true), cancel: () => finish(false) };
}
